import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";
import AlertSubscribe from "@/components/AlertSubscribe";
import NotFound from "@/pages/NotFound";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { CITIES, INTENTS, cityPath, findCity, findIntent } from "@/lib/cityLanding";

export default function CityLanding() {
  const { city: citySlug, intent: intentSlug } = useParams();
  const city = findCity(citySlug);
  const intent = findIntent(intentSlug);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!city || !intent) return;
    setLoading(true);
    window.scrollTo(0, 0);
    api
      .get("/properties", { params: { ...city.query, category: intent.category, limit: 24 } })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [city, intent]);

  const title = city && intent ? `${intent.label} in ${city.name}` : "Page not found";
  useSeo({
    title,
    noindex: !city || !intent,
    description:
      city && intent
        ? `Find ${intent.noun} in ${city.name} on Houses7.com. Verified listings with real photos, direct owner contact, no brokerage and instant alerts when a new match is listed.`
        : undefined,
    path: city && intent ? cityPath(city, intent) : undefined,
    jsonLd:
      city && intent
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Houses7.com", item: "https://houses7.com/" },
              { "@type": "ListItem", position: 2, name: city.name, item: `https://houses7.com/${city.slug}/${intent.slug}` },
              { "@type": "ListItem", position: 3, name: intent.label },
            ],
          }
        : null,
  });

  if (!city || !intent) return <NotFound />;

  const otherIntents = INTENTS.filter((i) => i.slug !== intent.slug);
  const otherCities = CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10" data-testid="city-landing-page">
      <nav className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
        <Link to="/" className="hover:text-foreground transition-colors duration-200">Home</Link>
        <span>/</span>
        <Link to="/properties" className="hover:text-foreground transition-colors duration-200">Properties</Link>
        <span>/</span>
        <span className="text-foreground font-semibold">{title}</span>
      </nav>

      <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="city-landing-heading">
        {intent.label} in {city.name}
      </h1>
      <p className="mt-3 max-w-2xl text-sm md:text-base text-muted-foreground leading-relaxed">
        {total > 0
          ? `${total} verified ${intent.noun} in ${city.name} — with real photographs, the owner's number on every listing and zero brokerage.`
          : `We're adding ${intent.noun} in ${city.name} every week. Set an alert below and we'll email you the moment one is listed.`}
      </p>
      <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
        <MapPin className="h-3.5 w-3.5" /> {city.name}, India
      </p>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="city-landing-intent-links">
        {otherIntents.map((i) => (
          <Link
            key={i.slug}
            to={cityPath(city, i)}
            className="rounded-full border border-border px-4 py-2 text-[13px] font-semibold hover:border-foreground/35 transition-colors duration-200"
          >
            {i.label} in {city.name}
          </Link>
        ))}
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-muted-foreground">Loading listings…</p>
      ) : items.length === 0 ? (
        <div className="mt-9 rounded-2xl border border-border p-10" data-testid="city-landing-empty">
          <p className="font-display text-xl">No {intent.label.toLowerCase()} listed in {city.name} yet</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Browse everything available, or set an alert and we'll email you the first match.
          </p>
          <Link to="/properties" className="mt-4 inline-block text-sm font-bold underline">
            See all properties
          </Link>
        </div>
      ) : (
        <div
          className="mt-9 grid gap-x-3.5 gap-y-6 sm:gap-x-6 sm:gap-y-9 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="city-landing-grid"
        >
          {items.map((p) => (
            <PropertyCard key={p.property_id} prop={p} />
          ))}
        </div>
      )}

      {items.length > 0 && (
        <Link to={`/properties?category=${intent.category}&city=${encodeURIComponent(city.query.city || city.name)}`}>
          <span className="mt-8 inline-block rounded-lg border border-border px-5 py-3 text-[13px] font-bold hover:border-foreground/35 transition-colors duration-200" data-testid="city-landing-see-all">
            Search with filters
          </span>
        </Link>
      )}

      <div className="mt-12">
        <AlertSubscribe filters={{ city: city.query.city || city.name, category: intent.category }} />
      </div>

      <section className="mt-14 border-t border-border pt-8">
        <h2 className="font-display text-lg">{intent.label} in other cities</h2>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2.5 text-[13px] text-muted-foreground" data-testid="city-landing-city-links">
          {otherCities.map((c) => (
            <Link key={c.slug} to={cityPath(c, intent)} className="hover:text-foreground transition-colors duration-200">
              {intent.label} in {c.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
